import { DndContext, DragEndEvent, DragOverEvent, KeyboardSensor, PointerSensor, closestCorners, useDroppable, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { flushSync } from 'react-dom'
import { ConnectedProps, connect } from 'react-redux'
import { useParams } from 'react-router-dom'
import { WhiteBox } from '../../../../../../../baseComponents/AppLayout/WhiteBox'
import LoadingSpinner from '../../../../../../../baseComponents/LoaderDecider/LoadingSpinner'
import { twinFetchPostJSON } from '../../../../../../../utils/globals/data'
import withLoading from '../../../../../../../utils/hoc/withLoading'
import useEditDeleteModal from '../../../../../../../utils/hooks/useEditDeleteModal'
import { AllReduxPayloads } from '../../../../../../../utils/reducers'
import { listenSocket, unlistenSocket } from '../../../../../../../utils/globals/socket'
import { EmployeesPayload } from '../../../../../../../utils/reducers/company/employees'
import { GroupPayload } from '../../../../../../../utils/reducers/groups/groups'
import { TaskCFieldPayload } from '../../../../../../../utils/reducers/company/taskCFields'
import './taskCustomFieldFlow.sass'
import { TaskCFieldValueModelType } from '@teinor/erp/types/company/task/taskCFieldValue'
import TaskFlowHeader from './TaskFlowHeader'
import TaskFlowSubHeader from './TaskFlowSubHeader'
import { TaskCFieldFiltersTy, TaskCFieldFlowUpdatedDataForApi, TaskCFieldValueDictForFlow } from './types'
import { TaskModelType } from '@teinor/erp/types/company/task'
import { CustomRenderTaskCustomerAndProject } from '../../../../../../../specificComponents/Task/TaskScreenTable/Subcomponents'
import { addBottomNavAppElement } from '../../../../../../../utils/reducers/reduxDispatch'
import { displayDateTime } from '../../../../../../../utils/globals/date'
import { faCalendar, faUser, faUserGroup } from '@fortawesome/pro-light-svg-icons'
import TwinIcon from '../../../../../../../baseComponents/TwinIcon'
import { CustomRenderRoundedImage } from '../../../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import TwinToolTip from '../../../../../../../baseComponents/TwinToolTip'
import useTwinTranslation from '../../../../../../../utils/hooks/useTwinTranslation'

interface TaskFlowProps extends TaskFlowLogicProps { }

const TaskFlow: React.FC<TaskFlowProps> = ({ taskCFields, ...logic }) => {
    const renderThis: JSX.Element[] = []
    const { data, getData, handleOnDragEnd, onHandleDragOver, sensors, myTaskCFieldId, filters, setFilters } = useTaskCFieldFlowLogic({ taskCFields, ...logic })
    const customField = taskCFields?.[myTaskCFieldId]
    if (!customField) {
        return null
    }
    let statuses: TaskCFieldValueModelType[] = []
    if (customField.active) {
        statuses = customField.TaskCFieldValues || []
        for (const element of statuses) {
            if (element) {
                renderThis.push(
                    <DroppableStatusColumn id={String(element.id)} status={element} taskCFields={data?.[element.id] || []} key={element.id} />
                )
            }
        }
    }

    return (
        <div className='py-36 px-50 flex flex-auto h-1 task_board_custom_field_flow'>
            <WhiteBox className='flex flex-auto flex-col py-40 px-60 w-1'>
                <TaskFlowHeader customField={customField} taskCFields={taskCFields} TaskCFieldTypeId={myTaskCFieldId} getData={getData} TaskCFieldTypes={taskCFields} FieldTypeId={myTaskCFieldId } {...{ ...logic }} />
                <TaskFlowSubHeader statuses={statuses} filters={filters} setFilters={setFilters} />
                {!data ?
                    <LoadingSpinner /> :
                    <div className='grid gap-10 flex-auto mt-30 overflow-x-auto' style={{ gridTemplateColumns: `repeat(${renderThis.length}, minmax(260px, 290px))` }}>
                        <DndContext
                            sensors={sensors}
                            onDragEnd={handleOnDragEnd}
                            onDragOver={onHandleDragOver}
                            collisionDetection={closestCorners}
                        >
                            {renderThis}
                        </DndContext>
                    </div>
                }
            </WhiteBox>
        </div>
    )
}

interface TaskFlowLogicProps extends ReduxTaskCFields { }

const useTaskCFieldFlowLogic = ({ taskCFields }: TaskFlowLogicProps) => {
    const [filters, setFilters] = useState <TaskCFieldFiltersTy>({
        searchValue: '',
        selectedEmployeeId: null,
        selectedGroupId: null
    })
    const myFiltersStr = JSON.stringify(filters)
    const [data, setData] = useState<TaskCFieldValueDictForFlow | null>(null)
    const { openModal, setOpenModal } = useEditDeleteModal()

    const { id } = useParams()
    const myTaskCFieldId = parseInt(String(id))

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { delay: 200, tolerance: 5 },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates
        })
    )

    const onHandleDragOver = useCallback((event: DragOverEvent) => {
        const { active, over } = event
        const activeId = active.id
        const overId = parseInt(String(over?.id) || '')
        const activeContainer = parseInt(active?.data?.current?.sortable?.containerId)
        let overContainer = 0
        if (over?.data?.current && 'sortable' in over?.data?.current) {
            overContainer = parseInt(over?.data?.current?.sortable?.containerId)
        } else {
            overContainer = parseInt(over?.data?.current?.columnId)
        }

        if (!activeContainer || !overContainer || activeContainer === overContainer) {
            return
        }
        setData((old) => {
            if (!old) {
                return null
            }
            const copy = JSON.parse(JSON.stringify(old))

            const activeItems = copy[activeContainer]
            const overItems = copy[overContainer]

            if (!copy[overContainer]) {
                copy[overContainer] = []
            }

            const activeIndex = activeItems.findIndex(
                (item: any) => item.id === activeId
            )
            const overIndex = overItems?.findIndex((item: any) => item.id === overId);
            let newIndex: number;
            if (copy && overId in copy) {
                newIndex = (overItems?.length || 0) + 1
            } else {
                const isBelowOverItem =
                    over &&
                    active.rect.current.translated &&
                    active.rect.current.translated.top > over.rect.top + over.rect.height;
                const modifier = isBelowOverItem ? 1 : 0;
                newIndex = overIndex >= 0 ? overIndex + modifier : overItems.length + 1;
            }
            return {
                ...copy,
                [activeContainer]: copy[activeContainer].filter(
                    (item: any) => item.id !== active.id
                ),
                [overContainer]: [
                    ...copy[overContainer].slice(0, newIndex),
                    copy[activeContainer][activeIndex],
                    ...copy[overContainer].slice(newIndex, copy[overContainer].length)
                ]
            }
        })

    }, [setData])

    const handleOnDragEnd = useCallback(async (event: DragEndEvent) => {
        const { active, over } = event
        const activeId = active.id
        const overId = parseInt(String(over?.id) || '')
        const activeContainer = parseInt(active?.data?.current?.sortable?.containerId)
        let overContainer = parseInt(over?.data?.current?.sortable?.containerId)
        let TaskCFieldWithValue: TaskCFieldFlowUpdatedDataForApi | null = null

        if (!activeContainer || !overContainer || activeContainer !== overContainer) {
            return
        }
        flushSync(async () => {
            await setData((old) => {
                if (!old) {
                    return null
                }
                let copy = JSON.parse(JSON.stringify(old))
                const activeIndex = copy[activeContainer].findIndex((item: any) => {
                    return item.id === activeId
                })
                const myOverContainer = copy[overContainer]
                const overIndex = myOverContainer.findIndex(
                    (item: any) => item.id === overId
                )

                if (!myOverContainer) {
                    copy[overContainer] = []
                }

                if (activeIndex !== overIndex) {
                    copy = { ...copy, [overContainer]: arrayMove(myOverContainer, activeIndex, overIndex) }
                }
                for (const key in myOverContainer) {
                    if ((active.id === copy[overContainer][key].id)) {
                        TaskCFieldWithValue = {
                            id: copy[overContainer][key].id,
                            name: copy[overContainer][key].name,
                            CurrentTaskCFieldValueId: overContainer,
                            TaskCField: myTaskCFieldId
                        }
                    }
                    copy[overContainer][key].originalContainer = overContainer
                }
                return copy
            })
            if (TaskCFieldWithValue) {
                await twinFetchPostJSON('/api/app/task/assignCustomFieldValueToTask', { TaskCFieldValueId: overContainer, TaskCFieldId: TaskCFieldWithValue.TaskCField, TaskId: activeId })
            }
        })
    }, [setData, myTaskCFieldId])

    const getData = useCallback(async () => {
        const { searchValue , selectedEmployeeId, selectedGroupId } = JSON.parse(myFiltersStr)
        const myTaskCFieldValueDict: TaskCFieldValueDictForFlow = {}
        let statusesWhere: number[] = []
        const customField = taskCFields?.[myTaskCFieldId]
        if (customField) {
            const statuses = customField.TaskCFieldValues || []
            for (const element of statuses) {
                if (element) {
                    statusesWhere.push(element.id)
                    myTaskCFieldValueDict[element.id] = []
                }
            }
        }
        const searchResult = '%' + searchValue + '%' 
        if (statusesWhere.length) {
            const result = await twinFetchPostJSON('/api/app/task/getAllTasks', {
                "limit": 100,
                "customFieldIds": [customField?.TaskCFieldValues?.[0]?.TaskCFieldId], 
                "offset": 0,
                "whereToParse": {
                    "generalSearchBar": searchResult,
                    "start_date": false,
                    "end_date": false, 
                    "finished": 1,
                    "visibles": 0,
                    "assigned": 1,
                    "EmployeeId": selectedEmployeeId,
                    "GroupId": selectedGroupId,
                    "subtask": 1,
                    "start_createdAt": false,
                    "end_createdAt": false,
                    [`customField_${myTaskCFieldId}`]: { "$in": [...statusesWhere] },
                    "deleted": false
                }, "orderToParse": [["id", "desc"]]
            })
            if (result) {
                for (const element of result) {
                    for (const task of element.TaskCFieldValTaskRels) {
                        if (myTaskCFieldValueDict[task.TaskCFieldValueId]) {
                            myTaskCFieldValueDict[task.TaskCFieldValueId].push({
                                ...element,
                                order: element.order,
                                originalContainer: task.TaskCFieldValueId
                            })
                        }
                    }
                }
                setData(myTaskCFieldValueDict)
            }
        } else {
            setData({})
        }
    }, [setData, myTaskCFieldId, taskCFields, myFiltersStr ])

    useEffect(() => {
        listenSocket('taskStatusFlow_' + myTaskCFieldId, getData, true)
        return () => {
            unlistenSocket('taskStatusFlow_' + myTaskCFieldId)
        }
    }, [getData, myTaskCFieldId])

    useEffect(() => {
        getData()
    }, [getData])

    return { sensors, data, getData, handleOnDragEnd, onHandleDragOver, myTaskCFieldId, openModal, setOpenModal, filters, setFilters }
}

interface DroppableStatusColumnProps {
    id: string
    status: TaskCFieldValueModelType
    taskCFields?: any
}

const DroppableStatusColumn: React.FC<DroppableStatusColumnProps> = ({ id, status, taskCFields }) => {
    const { setNodeRef } = useDroppable({
        id,
        data: {
            columnId: id
        }
    })
    const renderThis: JSX.Element[] = []
    for (const key in taskCFields) {
        const item = taskCFields[key]
        renderThis.push(
            <DraggableTask task={item} id={item?.id} index={item?.id} key={item?.id} originalContainer={item.originalContainer} />
        )
    }
    const statusColor = status.color
    return (
        <SortableContext items={taskCFields} strategy={rectSortingStrategy} id={id}>
            <div className='flex flex-col mr-15'>
                <div className='py-12 px-20 bg-gray-F7 border-t-4' style={{ borderColor: statusColor }}>
                    <div className='flex items-center gap-2'>
                        <h2 className='text-gray-51 text-18'>{status.name}</h2>
                        <div className='flex items-center justify-center h-20 min-w-20 px-5 ml-auto bg-gray-DD rounded-full light12 totals_info_circle'>{renderThis.length}</div>
                    </div>
                </div>
                <div className='flex flex-col flex-auto bg-gray-F7 mt-10 px-20 pt-5 pb-15'>
                    <div ref={setNodeRef} style={{ minHeight: '500px' }}>
                        {renderThis}
                    </div>
                </div>
            </div>
        </SortableContext>
    )
}

interface DraggableTaskProps extends DraggableTaskLogicProps {
    index: string
    originalContainer: number
}

const DraggableTask: React.FC<DraggableTaskProps> = ({ id, task, index, originalContainer }) => {
    const { dateTime, parsedId } = useDraggableTaskLogic({id, task})
    const { attributes, listeners, setNodeRef, transform } = useSortable({
        id,
        data: {
            taskId: id,
            index,
            task,
            originalContainer
        },
    });
    const style = {
        transform: CSS.Translate.toString(transform),
    }

    return (
        <div ref={setNodeRef} onClick={() => addBottomNavAppElement({ type: 'taskModal', key: 'taskModal-' + task.id, extraData: { id: task.id, opened: true } })} style={style} className='mt-12' {...listeners} {...attributes}>
            <CustomRenderTaskFlowCard dateTime={dateTime} parsedId={parsedId} task={task} />
        </div>
    )
}

interface DraggableTaskLogicProps {
    id: string
    task: TaskModelType
}

const useDraggableTaskLogic = ({ id, task }: DraggableTaskLogicProps) => {
    const parsedId = (task.id).toString()
    const dateTime = displayDateTime(new Date(task.createdAt))

    return { parsedId, dateTime }
}

interface CustomRenderTaskFlowCardProps {
    task: TaskModelType
    dateTime: string
    parsedId: string
}

const CustomRenderTaskFlowCard: React.FC<CustomRenderTaskFlowCardProps> = ({ task, dateTime, parsedId }) => {
    const defaultInfo = {
        'EmployeeId': '/user-png.png',
        'GroupId': '/group.png'
    }

    const profileEmployeePhoto = task.Employee ? (!task.Employee.profile_image ? defaultInfo.EmployeeId : task.Employee.profile_image) : false
    const profileGroupPhoto = task.Group ? (!task.Group.photo ? defaultInfo.GroupId : task.Group.photo) : false

    return (
        <div className='flex flex-col gap-y-5 rounded-lg bg-white px-17 py-10 task_board_custom_field_status_flow_card_content'>
            <div className='flex gap-x-5'>
                <div className="flex h-fit items-start">
                    <span className="text-green-43">{`#${task.id}`}</span>
                </div>
                <TaskFlowTaskName id={parsedId} name={task.name} />
            </div>
            {task.CustomerProject ? <CustomRenderTaskCustomerAndProject id={'main'} allRowData={task}/> : null}
            <div className='light12 text-gray-97'>
                <TwinIcon icon={faCalendar} className='mr-10' />
                <span>{dateTime}</span>
            </div>
            <div className='flex gap-x-10'>
                {profileEmployeePhoto ? <CustomRenderRoundedImage className='h-27 w-27' name={task.Employee?.name} icon={faUser} img={profileEmployeePhoto ?? '/user-png.png'} /> : null}
                {profileGroupPhoto ? <CustomRenderRoundedImage className='h-27 w-27' name={task.Group?.name} icon={faUserGroup} img={profileGroupPhoto ?? '/group.png'} /> : null}
            </div>
        </div>
    )
}

interface TaskFlowTaskNameProps {
    id: string
    name?: string
}

const TaskFlowTaskName: React.FC<TaskFlowTaskNameProps> = ({ id, name }) => {
    const { t } = useTwinTranslation()
    const taskName = name || t('title', 'Título')
    const idTooltip = useMemo(() => 'Tooltip_TaskFlowTaskName' + String(id), [id])
    return (
        <div className='flex-auto w-1'>
            <TwinToolTip id={idTooltip} place='top' className='custom_render_task_name_tooltip max-w-600'>
                <p className='text-wrap-initial'>{taskName}</p>
            </TwinToolTip>
            <div data-tooltip-id={idTooltip} className={'cursor-pointer twin_two_lines_elipsis ' + (!name ? 'text-gray-97' : '')}>{taskName}</div>
        </div>
    )
}

const TaskCFieldsDispatch = {
    setEmployees: (payload: EmployeesPayload) => ({ type: 'CHANGE_EMPLOYEE', payload }),
    setGroups: (payload: GroupPayload) => ({ type: 'CHANGE_GROUP', payload }),
    setTaskCFields: (payload: TaskCFieldPayload) => ({ type: 'CHANGE_TASKCFIELD', payload }),
}

export type ReduxTaskCFields = ConnectedProps<typeof TaskCFieldsConnect>
const mapTaskCFieldsConnector = (state: AllReduxPayloads) => ({ employees: state.employees, groups: state.groups, taskCFields: state.taskCFields })
const TaskCFieldsConnect = connect(mapTaskCFieldsConnector, TaskCFieldsDispatch)

const taskConnectLoading = withLoading(TaskFlow, [{ fetchUrl: '/api/app/employee/getAllEmployees', propName: 'employees', setFunctionName: 'setEmployees' }, { fetchUrl: '/api/app/group/getAllGroupsListing', propName: 'groups', setFunctionName: 'setGroups' }, { fetchUrl: '/api/app/task/customField/getAllTaskCFieldsComplete', propName: 'taskCFields', setFunctionName: 'setTaskCFields' }])
const TaskFlowConnect = TaskCFieldsConnect(taskConnectLoading)

export default TaskFlowConnect